import { useState } from 'react';
import { cn } from '@/lib/utils';
import { X, FileText, Code, Eye, Pencil, Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

export interface CanvasArtifact {
    id: string;
    title: string;
    content: string;
    contentType: 'markdown' | 'code' | 'mcp-app' | 'text';
    language?: string;
}

interface CanvasPaneProps {
    artifact: CanvasArtifact;
    onClose: () => void;
    onSave?: (artifact: CanvasArtifact) => void;
}

type TabMode = 'preview' | 'edit';

export function CanvasPane({ artifact, onClose, onSave }: CanvasPaneProps) {
    const [mode, setMode] = useState<TabMode>('preview');
    const [editContent, setEditContent] = useState(artifact.content);

    const handleSave = () => {
        onSave?.({ ...artifact, content: editContent });
    };

    const handleExport = () => {
        const blob = new Blob([editContent || artifact.content], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${artifact.title || 'export'}.${artifact.language || 'md'}`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const ContentIcon = artifact.contentType === 'code' ? Code : FileText;

    return (
        <div className="flex h-full flex-col rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
                <div className="flex items-center gap-2">
                    <ContentIcon className="h-4 w-4 text-slate-500" />
                    <h3 className="text-sm font-medium text-slate-800 dark:text-slate-200">
                        {artifact.title}
                    </h3>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={handleExport}
                        className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                        title="Export"
                    >
                        <Download className="h-3.5 w-3.5" />
                    </button>
                    <button
                        onClick={onClose}
                        className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
                        title="Close"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                </div>
            </div>

            <div className="flex items-center gap-1 border-b border-slate-200 px-4 dark:border-slate-700">
                <button
                    className={cn(
                        'flex items-center gap-1 border-b-2 px-2 py-2 text-xs font-medium transition-colors',
                        mode === 'preview'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700',
                    )}
                    onClick={() => setMode('preview')}
                >
                    <Eye className="h-3 w-3" /> Preview
                </button>
                <button
                    className={cn(
                        'flex items-center gap-1 border-b-2 px-2 py-2 text-xs font-medium transition-colors',
                        mode === 'edit'
                            ? 'border-blue-500 text-blue-600'
                            : 'border-transparent text-slate-500 hover:text-slate-700',
                    )}
                    onClick={() => setMode('edit')}
                >
                    <Pencil className="h-3 w-3" /> Edit
                </button>
            </div>

            <div className="flex-1 overflow-auto p-4">
                {mode === 'preview' ? (
                    artifact.contentType === 'mcp-app' ? (
                        <iframe
                            srcDoc={artifact.content}
                            className="h-full w-full rounded border border-slate-200"
                            sandbox="allow-scripts allow-same-origin"
                            title={artifact.title}
                        />
                    ) : (
                        <div className="prose prose-sm max-w-none prose-headings:text-slate-800 prose-a:text-blue-600 prose-code:rounded prose-code:bg-slate-200 prose-code:px-1.5 prose-code:py-0.5 prose-code:text-xs dark:prose-invert">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {artifact.content}
                            </ReactMarkdown>
                        </div>
                    )
                ) : (
                    <div className="flex h-full flex-col gap-2">
                        <textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="flex-1 resize-none rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-sm text-slate-800 focus:border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                        />
                        {onSave && (
                            <button
                                onClick={handleSave}
                                className="self-end rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-500"
                            >
                                Save
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
